import { useContext, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { Emoji } from "emoji-picker-react";
import EmojiPickerDialog from "../../../input/EmojiPickerDialog";
import Button from "../../../input/button";
import { PlannerContext } from "../../../../context/planner/PlannerContext";
import { ColourPicker } from "../../../pickers";
import {
  DEFAULT_LABEL_COLOR,
  PICKER_COLORS,
} from "../../../../constants/pickerColors";
import { getRandomColor } from "../../../../helpers/colorUtils";

const TemplateSelect = () => {
  const {
    availableTemplates,
    loadTemplate,
    updateTemplate,
    deleteTemplate,
    closeToolPanel,
  } = useContext(PlannerContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editEmoji, setEditEmoji] = useState("1f4cb");
  const [editColor, setEditColor] = useState(getRandomColor(PICKER_COLORS));
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const handleEditClick = (e, templateId) => {
    e.stopPropagation();
    const template = availableTemplates[templateId];
    setEditingTemplate(templateId);
    setEditName(template.name);
    setEditDescription(template.shortDescription || "");
    setEditEmoji(template.emoji || "1f4cb");
    setEditColor(template.color || DEFAULT_LABEL_COLOR);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (e, templateId) => {
    e.stopPropagation();
    setTemplateToDelete(templateId);
    setDeleteDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (editingTemplate && editName.trim()) {
      await updateTemplate(editingTemplate, {
        name: editName.trim(),
        shortDescription: editDescription.trim(),
        emoji: editEmoji,
        color: editColor,
      });
      setEditDialogOpen(false);
      setEditingTemplate(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (templateToDelete) {
      await deleteTemplate(templateToDelete);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleEmojiSelect = (emoji) => {
    setEditEmoji(emoji);
    setEmojiPickerOpen(false);
  };

  // Get custom templates (exclude built-in 'empty')
  const customTemplates = Object.keys(availableTemplates).filter(
    (id) => id !== "empty",
  );

  const hasCustomTemplates = customTemplates.length > 0;
  const showSearchBar = customTemplates.length > 5;

  const handleSelectTemplate = (templateId) => {
    loadTemplate(templateId);
    if (isMobile) {
      closeToolPanel();
    }
  };

  // Filter templates based on search query
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) {
      return customTemplates;
    }
    const query = searchQuery.toLowerCase().trim();
    return customTemplates.filter((templateId) => {
      const template = availableTemplates[templateId];
      return (
        template.name.toLowerCase().includes(query) ||
        (template.shortDescription &&
          template.shortDescription.toLowerCase().includes(query))
      );
    });
  }, [customTemplates, searchQuery, availableTemplates]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        flex: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          pb: 2,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h3">Choose a Template</Typography>

          <Box
            sx={{
              mt: 2,
              p: 2,
              [theme.breakpoints.down("md")]: {
                px: 1,
              },
            }}
          >
            <Card
              onClick={() => handleSelectTemplate("empty")}
              sx={{
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: 3,
                },
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1,
                  px: 2,
                  [theme.breakpoints.down("md")]: {
                    px: 1,
                    py: 0.75,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "grey.200",
                    color: "text.secondary",
                    mr: 2,
                  }}
                >
                  <AddCircleOutlineIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    Start Fresh
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Start with a blank day and add blocks as you go
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {hasCustomTemplates && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                Saved Templates
              </Typography>
              {showSearchBar && (
                <TextField
                  placeholder="Search templates..."
                  size="small"
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ mb: 1.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{ color: "text.secondary", fontSize: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              <Stack
                spacing={1}
                className="scroller"
                sx={{
                  maxHeight: 350,
                  overflowY: "auto",
                  p: 2,
                  [theme.breakpoints.down("md")]: {
                    px: 1,
                  },
                }}
              >
                {filteredTemplates.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    No templates found
                  </Typography>
                ) : (
                  filteredTemplates.map((templateId) => {
                    const template = availableTemplates[templateId];
                    return (
                      <Card
                        key={templateId}
                        onClick={() => handleSelectTemplate(templateId)}
                        sx={{
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          position: "relative",
                          overflow: "hidden",
                          flexShrink: 0,
                          "&:hover": {
                            boxShadow: 3,
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 6,
                            backgroundColor:
                              template.color || DEFAULT_LABEL_COLOR,
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            py: 1.5,
                            px: 2,
                            pl: 3,
                            "&:last-child": { pb: 1.5 },
                            [theme.breakpoints.down("md")]: {
                              px: 1,
                              pl: 1.5,
                              py: 1,
                              "&:last-child": { pb: 1 },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 2,
                              fontSize: 20,
                              [theme.breakpoints.down("md")]: {
                                mr: 1,
                                width: 28,
                                height: 28,
                              },
                            }}
                          >
                            <Emoji
                              unified={template.emoji || "1f4cb"}
                              size={24}
                            />
                          </Box>
                          <Box
                            sx={{
                              flex: 1,
                              [theme.breakpoints.down("md")]: {
                                mr: 1,
                              },
                            }}
                          >
                            <Typography variant="body1" sx={{ mb: 0.5 }}>
                              {template.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {template.shortDescription ||
                                `${template.dayStartTime} - ${template.dayEndTime}`}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleEditClick(e, templateId)}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: "primary.main" },
                                [theme.breakpoints.down("md")]: {
                                  color: "#fff",
                                  backgroundColor: "primary.main",
                                  padding: "8px",
                                  "&:hover": {
                                    backgroundColor: "primary.dark",
                                  },
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => handleDeleteClick(e, templateId)}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: "error.main" },
                                [theme.breakpoints.down("md")]: {
                                  color: "#fff",
                                  backgroundColor: "error.main",
                                  padding: "8px",
                                  "&:hover": {
                                    backgroundColor: "error.dark",
                                  },
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>

      {/* Edit Template Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 1301 }} // Nested — above ToolPanel dialog (1300) on mobile
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle>Edit Template</DialogTitle>
        <DialogContent
          sx={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
            [theme.breakpoints.down("md")]: {
              px: 2,
            },
          }}
        >
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                onClick={() => setEmojiPickerOpen(true)}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "grey.100",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "grey.200" },
                }}
              >
                <Emoji unified={editEmoji} size={32} />
              </Box>
              <TextField
                label="Template Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                fullWidth
                size="small"
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Color:
              </Typography>
              <ColourPicker
                selectedColor={editColor}
                onColorSelect={setEditColor}
                size={36}
              />
            </Box>
            <TextField
              label="Description (optional)"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Button
            text="Cancel"
            onClick={() => setEditDialogOpen(false)}
            color="grey"
          />
          <Button
            text="Save"
            onClick={handleEditSave}
            color="primary"
            disabled={!editName.trim()}
          />
        </DialogActions>
      </Dialog>

      {/* Emoji Picker Dialog */}
      <EmojiPickerDialog
        open={emojiPickerOpen}
        onClose={() => setEmojiPickerOpen(false)}
        onEmojiSelect={handleEmojiSelect}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 1301 }} // Nested — above ToolPanel dialog (1300) on mobile
      >
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent
          sx={{
            [theme.breakpoints.down("md")]: {
              px: 2,
            },
          }}
        >
          <Typography>
            Are you sure you want to delete "
            {templateToDelete && availableTemplates[templateToDelete]?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            text="Cancel"
            onClick={() => setDeleteDialogOpen(false)}
            color="grey"
          />
          <Button text="Delete" onClick={handleDeleteConfirm} color="red" />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateSelect;
